import { useEffect, useState, useCallback, useMemo } from "react";
import { getFirestore, collection, getDocs, doc, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import ProductCard from "./ProductCard";
import ProductForm from "./ProductForm";
import styles from "./ProductsDashboard.module.css";

export default function ProductsDashboard() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [filterBy, setFilterBy] = useState("all");
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const db = getFirestore();
  const auth = getAuth();

  // Check user role
  const checkUserRole = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserRole(userData.role);
        }
      }
    } catch (err) {
      console.error("Error checking user role:", err);
    }
  }, [auth, db]);

  // Fetch products from Firestore
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const productsSnapshot = await getDocs(collection(db, "products"));
      
      const productsData = productsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      setProducts(productsData);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    checkUserRole();
    fetchProducts();
  }, [fetchProducts, checkUserRole]);

  // Handle highlighting from search - triggers on any highlight event
  useEffect(() => {
    if (location.state?.highlightId && !loading && products.length > 0) {
      const scrollToElement = () => {
        const element = document.getElementById(`product-${location.state.highlightId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add(styles.highlighted);
          setTimeout(() => {
            element.classList.remove(styles.highlighted);
          }, 3000);
          return true;
        }
        return false;
      };

      // Try immediately first
      if (!scrollToElement()) {
        // If element not found, wait a bit for DOM to update
        const timer = setTimeout(() => {
          scrollToElement();
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [location.state, loading, products]);

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = products.filter(product => {
      const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (filterBy === "all") return matchesSearch;
      if (filterBy === "active") return matchesSearch && product.isActive !== false;
      if (filterBy === "inactive") return matchesSearch && product.isActive === false;
      
      return matchesSearch;
    });

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return (a.name || "").localeCompare(b.name || "");
        case "dateCreated":
          return new Date(b.dateCreated || 0) - new Date(a.dateCreated || 0);
        case "ingredients":
          // Count recipes for each product using embedded recipes array
          const aRecipeCount = (a.recipes || []).length;
          const bRecipeCount = (b.recipes || []).length;
          return bRecipeCount - aRecipeCount;
        default:
          return 0;
      }
    });
  }, [products, searchTerm, sortBy, filterBy]);

  // Calculate products with recipes
  const productsWithRecipes = useMemo(() => {
    return products.filter(product => 
      product.recipes && product.recipes.length > 0
    ).length;
  }, [products]);

  // After adding a product, refresh list
  const handleFormClose = () => {
    setShowForm(false);
    fetchProducts();
  };

  if (loading) return (
    <div className={styles.dashboardContainer}>
      <div className={styles.loadingSpinner}>Loading products...</div>
    </div>
  );

  // Show role-related message if user doesn't have permission
  if (userRole === 'new') {
    return (
      <div className={styles.dashboardContainer}>
        <div className={styles.noProducts}>
          <h3>Account Pending Approval</h3>
          <p>Your account is awaiting admin approval. Please contact an administrator to approve your account before you can access the products.</p>
          <p>Current role: {userRole}</p>
        </div>
      </div>
    );
  }

  // Show ProductForm
  if (showForm) {
    return <ProductForm onClose={handleFormClose} />;
  }

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.header}>
        <h2 className={styles.dashboardTitle}>Products Management</h2>
        <button
          className={styles.addButton}
          onClick={() => setShowForm(true)}
        >
          + Add Product
        </button>
      </div>

      {/* Stats Section */}
      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{products.length}</div>
          <div className={styles.statLabel}>Total Products</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {products.filter(p => p.isActive !== false).length}
          </div>
          <div className={styles.statLabel}>Active Products</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {productsWithRecipes}
          </div>
          <div className={styles.statLabel}>With Recipes</div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className={styles.filtersContainer}>
        <div className={styles.searchGroup}>
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <div className={styles.filterGroup}>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="name">Sort by Name</option>
            <option value="dateCreated">Sort by Date</option>
            <option value="ingredients">Sort by Recipe Complexity</option>
          </select>
          <select
            value={filterBy}
            onChange={(e) => setFilterBy(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Products</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>
      </div>
      
      <div className={styles.cardGrid}>
        {filteredAndSortedProducts.map((product) => (
          <div
            key={product.id}
            id={`product-${product.id}`}
            className={styles.productCardWrapper}
          >
            <ProductCard
              product={product}
              onClick={() => navigate(`/products/${product.id}`)}
            />
          </div>
        ))}
      </div>

      {filteredAndSortedProducts.length === 0 && !loading && (
        <div className={styles.noProducts}>
          {searchTerm || filterBy !== "all" ? (
            <p>No products match your current filters. Try adjusting your search or filters.</p>
          ) : (
            <p>No products found. Add your first product to get started!</p>
          )}
        </div>
      )}
    </div>
  );
}
